/* ---------------------------------------------------------------------------
 * ZH MEDIA
 *
 * Drives the video case of snippets/zh-media.liquid, wherever in the theme that
 * slot happens to sit. Four jobs:
 *
 *   1. Pick the file that matches the breakpoint before anything is fetched.
 *   2. Play only while the slot is on screen.
 *   3. Honour prefers-reduced-motion by leaving the video paused.
 *   4. Run the pause control that WCAG 2.2.2 requires for a looping autoplay
 *      video.
 *
 * Everything hangs off [data-zh-media], so a section that renders the snippet
 * needs no JS of its own. Idempotent per element: initialising twice is a no-op,
 * which is what makes the Shopify editor's section reload safe.
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

  var INITIALISED = 'zhMediaReady';

  function reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function videoOf(scope) {
    return scope.querySelector('video');
  }

  /* The label and the glyph both follow the actual state rather than the click,
     so a video the browser refused to start does not end up labelled "pause". */
  function setToggleState(button, playing) {
    var label = playing ? button.getAttribute('data-label-pause') : button.getAttribute('data-label-play');
    if (label) {
      button.setAttribute('aria-label', label);
      button.setAttribute('title', label);
    }
    /* U+FE0E hinter dem Dreieck erzwingt die Textdarstellung. U+25B6 hat eine
       Emoji-Praesentation, und iOS rendert es ohne den Selector als farbiges
       Emoji statt als Glyphe in der Schriftfarbe des Knopfs. U+2759 (das
       Pausenzeichen) hat keine, braucht also nichts. */
    button.textContent = playing ? '❙❙' : '▶︎';
  }

  /* Source swap.
   *
   * Only for a slot that carries both files. Setting `src` on the element wins
   * over the <source> child that the markup ships, which is what lets the
   * desktop file stay the no-JS default. With preload="none" this runs before
   * any byte is fetched, so exactly one of the two files is ever downloaded.
   *
   * load() is called only on a real change, otherwise a resize inside the same
   * breakpoint would restart playback. */
  function applySource(scope, video) {
    var desktop = scope.getAttribute('data-zh-media-desktop');
    var mobile = scope.getAttribute('data-zh-media-mobile');
    if (!desktop || !mobile) return;

    var breakpoint = parseInt(scope.getAttribute('data-zh-media-breakpoint'), 10) || 699;
    var wanted = window.matchMedia('(max-width: ' + breakpoint + 'px)').matches ? mobile : desktop;

    if (video.getAttribute('src') === wanted) return;
    var wasPlaying = !video.paused;
    video.setAttribute('src', wanted);
    video.load();
    if (wasPlaying) play(video);
  }

  /* Autoplay is blocked often enough that the rejection has to be swallowed:
     an unhandled rejection here would show up as a console error on every page
     that carries a video slot. The poster frame stays visible either way. */
  function play(video) {
    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
  }

  /* Playback follows the viewport.
   *
   * A page can hold a dozen of these slots, and a dozen videos decoding at once
   * is what makes a page feel broken on a phone. Anything off screen is paused,
   * which also means a slot far down the page never starts at all until it is
   * reached.
   *
   * A video the visitor paused on purpose stays paused: that is what the
   * data-zh-media-held flag records. Without it, scrolling past and back would
   * undo the pause control and with it the WCAG 2.2.2 mechanism. */
  var observer = null;

  /* The observer holds its targets strongly, and the editor replaces a section's
     markup on every change, so the copies it leaves behind would pile up for the
     length of an editing session. Hence the roster and the sweep below, run before
     each new batch is registered.

     isConnected is read defensively: anything that is not explicitly detached stays
     on the roster, so a host without the property simply keeps the old behaviour. */
  var watched = [];

  function sweep() {
    if (!observer) return;
    for (var i = watched.length - 1; i >= 0; i--) {
      if (watched[i].isConnected === false) {
        if (typeof observer.unobserve === 'function') observer.unobserve(watched[i]);
        watched.splice(i, 1);
      }
    }
  }

  function watch(scope, video) {
    if (!('IntersectionObserver' in window)) {
      play(video);
      return;
    }
    if (!observer) {
      /* window-qualified on purpose. A bare global works in a browser but not when
         the asset is evaluated against a constructed scope, which is how the unit
         suite runs it, and the qualified form is the same lookup either way. */
      observer = new window.IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var element = entry.target;
            var media = videoOf(element);
            if (!media) return;
            if (entry.isIntersecting) {
              if (element.getAttribute('data-zh-media-held') !== 'true') play(media);
            } else {
              media.pause();
            }
          });
        },
        { rootMargin: '100px 0px' }
      );
    }
    observer.observe(scope);
    watched.push(scope);
  }

  /* Swap-only mode.
   *
   * For the Impact sections that bring their own player: sections/video,
   * media-grid, media-with-text, multi-column, main-collection and blocks/video
   * wrap a video_tag in <video-media>, which has its own autoplay handling and
   * also supports a YouTube or Vimeo embed instead. Those are not rebuilt on
   * zh-media, they only needed the one thing markup cannot do: a different file
   * below the breakpoint. <video> ignores the media attribute on a <source>, so
   * this is the only way.
   *
   * Hence a second, deliberately dumb hook. It swaps the file and touches nothing
   * else: no intersection observer, no muted handling, no pause control, so it
   * cannot fight the element's own logic. */
  function initSwaps(root) {
    var scopes = (root || document).querySelectorAll('[data-zh-media-swap]');

    Array.prototype.forEach.call(scopes, function (scope) {
      if (scope.dataset[INITIALISED] === 'true') return;
      var video = videoOf(scope);
      if (!video) return;
      scope.dataset[INITIALISED] = 'true';
      applySource(scope, video);
    });
  }

  /* The pause control inside a linked card.
   *
   * Nine slots in the theme sit inside an <a>: category tiles, collection list,
   * image link blocks, product cards, the magazine badges, the product track, the
   * mega menu promo. A <button> in an <a> is interactive content nested in
   * interactive content, which is invalid HTML and an axe-core nested-interactive
   * violation, and it leaves a screen reader announcing a button inside a link.
   *
   * Deleting the control is not an option: it is the WCAG 2.2.2 mechanism for a
   * looping autoplay video. So it is moved out instead, to just after the anchor.
   * It is invisible until focused anyway, so where exactly it sits does not show,
   * and the tab order stays sensible: card, then its pause control.
   *
   * Done here and not in Liquid because the snippet cannot see whether its caller
   * wrapped it in a link, and tagging nine call sites by hand is nine chances to
   * miss one, plus a tenth the next time someone adds a card.
   *
   * The video reference travels with the button, since moving it out of the
   * wrapper also moves it out of the scope the click handler would search.
   *
   * The move has a price the CSS has to pay. The control is positioned against
   * .zh-media, and that box stays behind inside the link, so the offsets it carries
   * would resolve against whatever ancestor happens to be positioned, or against the
   * initial containing block: on focus the control appears nowhere near its card, and
   * on a long page often outside the viewport entirely. Hence the marker. The CSS
   * gives a marked control no offsets at all and lets it come back as a fixed overlay
   * once it is focused, which is a place the visitor can actually see.
   *
   * Putting it back over the card would mean restructuring the nine call sites to the
   * link-overlay pattern: the card stops being the <a> and becomes a positioned box
   * that the link covers with a pseudo-element. Only then can a control sit over the
   * media and outside the link at the same time. */
  function unnestToggle(scope, video) {
    var toggle = scope.querySelector('[data-zh-media-toggle]');
    if (!toggle) return null;

    toggle.zhMediaVideo = video;

    var anchor = scope.closest ? scope.closest('a') : null;
    if (anchor && anchor.parentNode && typeof anchor.insertAdjacentElement === 'function') {
      anchor.insertAdjacentElement('afterend', toggle);
      toggle.setAttribute('data-zh-media-unnested', 'true');
    }

    return toggle;
  }

  function init(root) {
    initSwaps(root);
    sweep();

    var scopes = (root || document).querySelectorAll('[data-zh-media]');

    Array.prototype.forEach.call(scopes, function (scope) {
      if (scope.dataset[INITIALISED] === 'true') return;
      var video = videoOf(scope);
      if (!video) return;
      scope.dataset[INITIALISED] = 'true';

      applySource(scope, video);

      var toggle = unnestToggle(scope, video);
      var autoplays = video.hasAttribute('autoplay');

      /* Reduced motion means the video does not start by itself. The autoplay
         attribute has to come off the element, pausing alone is not enough:
         the browser acts on the attribute, not on our call order. */
      if (autoplays && reduced()) {
        video.removeAttribute('autoplay');
        video.pause();
        scope.setAttribute('data-zh-media-held', 'true');
        autoplays = false;
      }

      /* Muted has to be set as a property too. Safari reads the property, not
         the attribute, when it decides whether autoplay is allowed. */
      if (autoplays) {
        video.muted = true;
        video.defaultMuted = true;
        watch(scope, video);
      }

      if (toggle) {
        setToggleState(toggle, autoplays);
        video.addEventListener('play', function () {
          setToggleState(toggle, true);
        });
        video.addEventListener('pause', function () {
          setToggleState(toggle, false);
        });
      }
    });
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest ? event.target.closest('[data-zh-media-toggle]') : null;
    if (!toggle) return;

    /* The reference first, the DOM walk second: a control that was moved out of a
       linked card no longer has the wrapper as an ancestor. */
    var scope = toggle.closest('[data-zh-media]');
    var video = toggle.zhMediaVideo || (scope ? videoOf(scope) : null);
    if (!video) return;

    /* The held flag lives on the wrapper, so it is resolved from the video when the
       control has been moved away from it. */
    var holder = scope || (video.closest ? video.closest('[data-zh-media]') : null);

    if (video.paused) {
      if (holder) holder.setAttribute('data-zh-media-held', 'false');
      play(video);
    } else {
      if (holder) holder.setAttribute('data-zh-media-held', 'true');
      video.pause();
    }
    setToggleState(toggle, !video.paused);
  });

  /* One listener for every slot on the page, hence the query on resize rather
     than a matchMedia listener per element.

     Coalesced to one pass per frame. resize fires per pixel while a window is
     dragged, and on a phone for every show and hide of the browser bar, so an
     unthrottled document-wide query here is paid on exactly the device that can
     least afford it. A frame is the right grain: nothing below it can be seen. */
  var resizeQueued = false;

  function refreshSources() {
    resizeQueued = false;
    var scopes = document.querySelectorAll('[data-zh-media-desktop]');
    Array.prototype.forEach.call(scopes, function (scope) {
      var video = videoOf(scope);
      if (video) applySource(scope, video);
    });
  }

  window.addEventListener('resize', function () {
    if (resizeQueued) return;
    resizeQueued = true;
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(refreshSources);
    } else {
      window.setTimeout(refreshSources, 100);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* The editor replaces a section's markup wholesale, so the fresh copy needs
     initialising. The guard flag lives on the element and goes away with it. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  /* The product page replaces its own markup too.
   *
   * Switching product on a combined listing takes the `productChange` path in
   * theme.js, which does replaceWith() on the whole <product-rerender> subtree and
   * dispatches no shopify:section:load. A video in the product's image block would
   * come back without any wiring: nothing starts it, and its pause button would be
   * dead, which takes the WCAG 2.2.2 mechanism with it.
   *
   * Caught here rather than patched into theme.js, which is the Impact bundle and
   * gets overwritten by the next theme update.
   *
   * Two details make this work. The capture phase, because product:rerender is
   * dispatched on the form WITHOUT bubbles, and a non-bubbling event still travels
   * the capture path from the root down to its target. And the timeout, because
   * capture runs BEFORE the listener that swaps the DOM: by the next task the new
   * markup is in place. init() is idempotent, so a spurious run costs one
   * querySelectorAll. */
  document.addEventListener(
    'product:rerender',
    function () {
      window.setTimeout(function () {
        init();
      }, 0);
    },
    true
  );

  window.zhMediaInit = init;
})();
